/**
 * 模拟交易设置页面
 * 路径：/system/simulate-settings
 *
 * 交互逻辑：
 * 1. 顶部展示交易所卡片（OKX/Bybit/Hyperliquid）
 * 2. 点击交易所卡片进入该交易所配置，卡片切换激活状态
 * 3. 下方展示 Tab（合约/现货），切换查看不同交易类型的品种列表
 * 4. 品种列表支持新增/删除操作，每个品种一条独立记录
 *
 * @author page
 * @date 2026-04-23
 */

import React, {useEffect, useState} from 'react';
import {Button, Card, Form, Input, message, Modal, Select, Table, Tabs, Tooltip, Typography,} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import {simulateExchangeConfigService, simulateSymbolConfigService} from '../../services/simulateService';
import type {ExchangeConfig, SymbolConfigRecord} from '../../types/simulate';
import {SUPPORTED_EXCHANGES, SUPPORTED_PRODUCT_TYPES} from '../../types/simulate';

const {Text} = Typography;

/**
 * 交易所卡片组件的属性接口
 */
interface ExchangeCardProps {
    /** 交易所名称 */
    name: string;
    /** 是否处于激活状态 */
    isActive: boolean;
    /** 是否已在配置中启用 */
    isEnabled: boolean;
    /** 点击卡片时的回调 */
    onClick: () => void;
    /** 切换启用/禁用状态的回调 */
    onToggleEnable: () => void;
}

/**
 * 交易所 Logo 路径映射
 */
const EXCHANGE_LOGO_MAP: Record<string, string> = {
    'OKX': '/apikeys/okx.png',
    'Bybit': '/apikeys/bybit.svg',
    'Hyperliquid': '/apikeys/hyperliquid.svg',
};

/**
 * 交易所卡片组件
 * 中央展示交易所 Logo，右下角显示启用状态和操作按钮
 */
const ExchangeCard: React.FC<ExchangeCardProps> = ({name, isActive, isEnabled, onClick, onToggleEnable}) => {
    /** 获取交易所 Logo 路径 */
    const logoSrc = EXCHANGE_LOGO_MAP[name] || '';

    return (
        <Card
            hoverable={isEnabled}
            onClick={isEnabled ? onClick : undefined}
            style={{
                cursor: isEnabled ? 'pointer' : 'not-allowed',
                border: isActive ? '2px solid #F0B429' : '1px solid var(--pt-border)',
                background: isActive ? 'rgba(240, 180, 41, 0.08)' : 'var(--pt-bg-panel)',
                transition: 'all 0.3s ease',
                height: '180px',
                position: 'relative',
                overflow: 'hidden',
                opacity: isEnabled ? 1 : 0.5,
            }}
            styles={{body: {padding: 0, height: '100%'}}}
        >
            {/* 中央 Logo 区域 */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '20px',
            }}>
                <img
                    src={logoSrc}
                    alt={name}
                    style={{
                        maxWidth: '80%',
                        maxHeight: '80%',
                        objectFit: 'contain',
                        filter: isEnabled ? 'none' : 'grayscale(100%) opacity(0.5)',
                        transition: 'all 0.3s ease',
                    }}
                />
            </div>

            {/* 右下角状态和操作区域 */}
            <div style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
            }}>
                {/* 启用/禁用状态图标 */}
                <Tooltip title={isEnabled ? '已启用，点击禁用' : '未启用，点击启用'}>
                    <Button
                        type="text"
                        size="small"
                        icon={isEnabled ? (
                            <CheckCircleOutlined style={{color: 'var(--pt-bull)', fontSize: '18px'}}/>
                        ) : (
                            <CloseCircleOutlined style={{color: 'var(--pt-text-secondary)', fontSize: '18px'}}/>
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleEnable();
                        }}
                    />
                </Tooltip>
            </div>
        </Card>
    );
};

/**
 * 模拟交易设置页面组件
 */
const SimulateSettings: React.FC = () => {
    // ==================== 状态管理 ====================

    /** 配置数据加载状态 */
    const [loading, setLoading] = useState(false);

    /** 当前选中的交易所（null 表示未选择任何交易所） */
    const [selectedExchange, setSelectedExchange] = useState<string | null>(null);
    /** 当前选中的交易类型（contract 合约 / spot 现货） */
    const [selectedProductType, setSelectedProductType] = useState<string>('contract');

    /** 交易所配置列表 */
    const [exchangeConfigs, setExchangeConfigs] = useState<ExchangeConfig[]>([]);
    /** 已配置的品种列表（新表模式，每个品种一条记录） */
    const [symbolRecords, setSymbolRecords] = useState<SymbolConfigRecord[]>([]);

    /** 添加品种弹窗显示状态 */
    const [addModalVisible, setAddModalVisible] = useState(false);
    /** 添加品种表单实例 */
    const [addForm] = Form.useForm();

    /** 编辑品种弹窗显示状态 */
    const [editModalVisible, setEditModalVisible] = useState(false);
    /** 编辑品种表单实例 */
    const [editForm] = Form.useForm();
    /** 当前编辑的品种记录 */
    const [editingRecord, setEditingRecord] = useState<SymbolConfigRecord | null>(null);

    // ==================== 数据加载 ====================

    /**
     * 页面加载时获取配置数据
     */
    useEffect(() => {
        loadConfigs();
    }, []);

    /**
     * 从后端加载所有配置项
     */
    const loadConfigs = async () => {
        try {
            setLoading(true);
            // 并行加载交易所配置和品种配置
            const [exchangeResponse, symbolResponse] = await Promise.all([
                simulateExchangeConfigService.getAllExchanges(),
                simulateSymbolConfigService.getAllSymbols(),
            ]);

            // 解析交易所配置
            const exchangeApiResponse = exchangeResponse.data;
            if ('0' === exchangeApiResponse.code) {
                const exchanges = exchangeApiResponse.data;
                setExchangeConfigs(exchanges);
                // 默认选中第一个已启用的交易所（优先 OKX）
                const okxConfig = exchanges.find(c => 'OKX' === c.exchangeName && 0 === c.enabled);
                if (okxConfig && !selectedExchange) {
                    setSelectedExchange('OKX');
                } else if (!selectedExchange && exchanges.length > 0) {
                    const firstEnabled = exchanges.find(c => 0 === c.enabled);
                    if (firstEnabled) {
                        setSelectedExchange(firstEnabled.exchangeName);
                    }
                }
            }

            // 解析品种配置
            const symbolApiResponse = symbolResponse.data;
            if ('0' === symbolApiResponse.code) {
                setSymbolRecords(symbolApiResponse.data);
            }
        } catch (error) {
            console.error('加载配置失败:', error);
            message.error('加载配置失败，请检查后端服务是否正常运行');
        } finally {
            setLoading(false);
        }
    };

    // ==================== 交易所操作 ====================

    /**
     * 点击交易所卡片，切换选中状态
     * @param exchange 交易所名称
     */
    const handleExchangeClick = (exchange: string) => {
        setSelectedExchange(selectedExchange === exchange ? null : exchange);
    };

    /**
     * 切换交易所的启用/禁用状态
     * @param exchange 交易所名称
     */
    const handleToggleExchange = async (exchange: string) => {
        try {
            const currentConfig = exchangeConfigs.find(c => c.exchangeName === exchange);
            const newEnabled = currentConfig && 0 === currentConfig.enabled ? 1 : 0;

            await simulateExchangeConfigService.updateExchangeStatus(exchange, newEnabled);

            setExchangeConfigs(exchangeConfigs.map(c =>
                c.exchangeName === exchange ? {...c, enabled: newEnabled} : c
            ));

            if (selectedExchange === exchange && 1 === newEnabled) {
                setSelectedExchange(null);
            }
        } catch (error: any) {
            console.error('更新交易所状态失败:', error);
            message.error('更新交易所状态失败: ' + (error.message || '未知错误'));
        }
    };

    // ==================== 品种操作 ====================

    /**
     * 获取当前交易所+交易类型下的品种列表
     */
    const getCurrentSymbols = (): SymbolConfigRecord[] => {
        if (!selectedExchange) return [];
        return symbolRecords.filter(
            s => s.exchangeName === selectedExchange && s.productType === selectedProductType
        );
    };

    /**
     * 打开添加品种弹窗
     */
    const handleAddSymbol = () => {
        addForm.resetFields();
        addForm.setFieldsValue({
            exchange: selectedExchange,
            type: selectedProductType,
        });
        setAddModalVisible(true);
    };

    /**
     * 确认添加品种
     */
    const handleAddSymbolConfirm = async () => {
        try {
            const values = await addForm.validateFields();

            // 调用后端 API 新增品种
            await simulateSymbolConfigService.addSymbol({
                exchangeName: values.exchange,
                productType: values.type,
                symbol: values.symbol,
                coinSymbol: values.coinSymbol,
                enabled: 0,
                sortOrder: symbolRecords.length,
            });

            setAddModalVisible(false);
            message.success('添加品种成功');

            // 重新加载数据
            await loadConfigs();
        } catch (error: any) {
            console.error('添加品种失败:', error);
            message.error('添加品种失败: ' + (error.message || '未知错误'));
        }
    };

    /**
     * 打开编辑品种弹窗
     * @param record 品种记录
     */
    const handleEditSymbol = (record: SymbolConfigRecord) => {
        setEditingRecord(record);
        editForm.setFieldsValue({
            symbol: record.symbol,
            coinSymbol: record.coinSymbol || '',
        });
        setEditModalVisible(true);
    };

    /**
     * 确认编辑品种
     */
    const handleEditConfirm = async () => {
        try {
            const values = await editForm.validateFields();
            if (editingRecord) {
                await simulateSymbolConfigService.updateSymbol(editingRecord.id, {
                    exchangeName: editingRecord.exchangeName,
                    productType: editingRecord.productType,
                    symbol: values.symbol,
                    coinSymbol: values.coinSymbol || null,
                    enabled: editingRecord.enabled,
                    sortOrder: editingRecord.sortOrder,
                });
                message.success('修改品种成功');
                setEditModalVisible(false);
                await loadConfigs();
            }
        } catch (error: any) {
            console.error('修改品种失败:', error);
            message.error('修改品种失败: ' + (error.message || '未知错误'));
        }
    };

    /**
     * 删除品种
     * @param record 品种记录
     */
    const handleDeleteSymbol = (record: SymbolConfigRecord) => {
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除品种 ${record.symbol} 吗？`,
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await simulateSymbolConfigService.deleteSymbol(record.id);
                    message.success('删除品种成功');
                    // 重新加载数据
                    await loadConfigs();
                } catch (error: any) {
                    console.error('删除品种失败:', error);
                    message.error('删除品种失败: ' + (error.message || '未知错误'));
                }
            },
        });
    };

    // ==================== 品种列表表格列定义 ====================

    /** 品种列表表格列配置 */
    const symbolColumns = [
        {
            title: '交易对',
            dataIndex: 'symbol',
            key: 'symbol',
            width: 150,
        },
        {
            title: '交易符号',
            dataIndex: 'coinSymbol',
            key: 'coinSymbol',
            width: 180,
            render: (text: string) => text || '-',
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (_: unknown, record: SymbolConfigRecord) => (
                <>
                    <Tooltip title="编辑品种">
                        <Button
                            type="text"
                            icon={<EditOutlined/>}
                            onClick={() => handleEditSymbol(record)}
                        />
                    </Tooltip>
                    <Tooltip title="删除品种">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined/>}
                            onClick={() => handleDeleteSymbol(record)}
                        />
                    </Tooltip>
                </>
            ),
        },
    ];

    // ==================== Tab 配置 ====================

    /**
     * Tab 切换项配置
     */
    const tabItems = SUPPORTED_PRODUCT_TYPES.map(type => ({
        key: type.key,
        label: type.label,
        children: (
            <div>
                {/* 工具栏 */}
                <div style={{marginBottom: '16px', display: 'flex', justifyContent: 'space-between'}}>
                    <Text type="secondary">
                        已配置 {getCurrentSymbols().length} 个品种
                    </Text>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={handleAddSymbol}
                        disabled={!selectedExchange}
                    >
                        添加品种
                    </Button>
                </div>
                {/* 品种列表 */}
                <Table
                    columns={symbolColumns}
                    dataSource={getCurrentSymbols()}
                    rowKey="id"
                    pagination={{pageSize: 10}}
                    size="middle"
                    locale={{emptyText: '暂无品种，请点击右上角添加'}}
                />
            </div>
        ),
    }));

    // ==================== 页面渲染 ====================

    return (
        <div style={{padding: '24px'}}>
            {/* 页面标题 */}
            <div style={{marginBottom: '24px'}}>
                <div style={{fontSize: '24px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center'}}>
                    <SettingOutlined style={{marginRight: '8px'}}/>
                    模拟交易设置
                </div>
                <Text type="secondary">选择交易所，配置合约和现货交易对</Text>
            </div>

            {/* 交易所卡片区域 */}
            <div style={{marginBottom: '24px'}}>
                <Text strong style={{display: 'block', marginBottom: '12px', fontSize: '16px'}}>
                    选择交易所
                </Text>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px'}}>
                    {SUPPORTED_EXCHANGES.map(exchange => {
                        const exchangeConfig = exchangeConfigs.find(c => c.exchangeName === exchange);
                        const isEnabled = exchangeConfig ? 0 === exchangeConfig.enabled : false;
                        return (
                            <ExchangeCard
                                key={exchange}
                                name={exchange}
                                isActive={selectedExchange === exchange}
                                isEnabled={isEnabled}
                                onClick={() => handleExchangeClick(exchange)}
                                onToggleEnable={() => handleToggleExchange(exchange)}
                            />
                        );
                    })}
                </div>
            </div>

            {/* Tab 区域：选中交易所后直接展示，无外框 */}
            {selectedExchange && (
                <Tabs
                    activeKey={selectedProductType}
                    onChange={setSelectedProductType}
                    items={tabItems}
                    size="large"
                />
            )}

            {/* 添加品种弹窗 */}
            <Modal
                title="添加品种"
                open={addModalVisible}
                onOk={handleAddSymbolConfirm}
                onCancel={() => setAddModalVisible(false)}
                okText="确定"
                cancelText="取消"
            >
                <Form form={addForm} layout="vertical">
                    <Form.Item
                        name="exchange"
                        label="交易所"
                        rules={[{required: true, message: '请选择交易所'}]}
                    >
                        <Select placeholder="请选择交易所" disabled>
                            {SUPPORTED_EXCHANGES.map(exchange => (
                                <Select.Option key={exchange} value={exchange}>
                                    {exchange}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="type"
                        label="交易类型"
                        rules={[{required: true, message: '请选择交易类型'}]}
                    >
                        <Select placeholder="请选择交易类型" disabled>
                            {SUPPORTED_PRODUCT_TYPES.map(type => (
                                <Select.Option key={type.key} value={type.key}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="symbol"
                        label="交易对"
                        rules={[
                            {required: true, message: '请输入交易对'},
                            {pattern: /^[A-Z]+\/[A-Z]+$/, message: '格式错误，应为 BTC/USDT 格式'},
                        ]}
                    >
                        <Input placeholder="请输入交易对，如 BTC/USDT"/>
                    </Form.Item>
                    <Form.Item
                        name="coinSymbol"
                        label="真实符号"
                        tooltip="如合约符号 BTC/USDT:USDT 或 BTC-USDT-SWAP"
                    >
                        <Input placeholder="如 BTC/USDT:USDT"/>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑品种弹窗 */}
            <Modal
                title="编辑品种"
                open={editModalVisible}
                onOk={handleEditConfirm}
                onCancel={() => setEditModalVisible(false)}
                okText="确定"
                cancelText="取消"
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item
                        name="symbol"
                        label="交易对"
                        rules={[
                            {required: true, message: '请输入交易对'},
                            {pattern: /^[A-Z]+\/[A-Z]+$/, message: '格式错误，应为 BTC/USDT 格式'},
                        ]}
                    >
                        <Input placeholder="请输入交易对，如 BTC/USDT"/>
                    </Form.Item>
                    <Form.Item
                        name="coinSymbol"
                        label="真实符号"
                        tooltip="如合约符号 BTC/USDT:USDT 或 BTC-USDT-SWAP"
                    >
                        <Input placeholder="如 BTC/USDT:USDT"/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SimulateSettings;
